import { useEffect, useState } from "react";
import ProductDataSection from "./ProductDataSection";
import BaseDialog, { Field, FieldSet } from "../modelDialogs/BaseDialog";

import KnxProductData, { ConvertVersion } from "../KnxProductdata";

import { isUiModeDeveloper, isUiModeExpert, isUiModeProductAdmin } from "../uiMode";
import { itx, noItx } from "../translationItx/itx";
import ImageAndName from "./ProductList/ImageAndName";
import { cleanDecode } from "../utils/CommonUtils";
import { getTranslatedMetadata } from "../models/KnxprodLoader";
import { compareVersions } from "../utils/CommonUtils";
import ProductStorageHandler from "../persistence/ProductStorageHandle";
import Device from "../models/Device";
import { showToast } from "./Toasts";
import { getTemplateGroup } from "../models/TemplateGroup";
import DeviceTemplate from "../models/DeviceTemplate";
import { DeviceClasses } from "../models/DeviceClasses";
import { ChannelType } from "../models/ChannelType";
import { getDecodedMetadata } from "../models/KnxprodLoader";
import { isDevelopmentEnvironment } from "../estada-config-debug";
import UI_KnxProductVoltusData from "./UI_KnxProductVoltusData";
import TechnicalManualLink from "./TechnicalManualLink";
import KnxRawProductDataViewer, { formatKnxRawProductDataText } from "./KnxRawProductDataViewer";

function formatMetadataLastModified(lastModified: any): string {
  if (!lastModified) return "";

  const date = new Date(lastModified);
  if (!Number.isNaN(date.getTime())) return date.toLocaleString();

  return String(lastModified);
}

export default function UI_KnxProductData(props: {
  productData?: KnxProductData;
  orderInformation?: any;
  device?: Device;
}) {
  const { productData, orderInformation, device } = props;

  // New Variables for checking if the selected product version is uptodate
  const [isLoaded, setIsLoaded] = useState(false);
  const [azureProductDataMap, setAzureProductDataMap] = useState(new Map());
  const [azureOrderNumberMap, setAzureOrderNumberMap] = useState(new Map());
  const productInfo = productData?.productInfo;

  const isDeveloper = isUiModeDeveloper();
  const [cleanedRawProductData, setCleanedRawProductData] = useState<any>(undefined);

  useEffect(() => {
    if (!productData) {
      setCleanedRawProductData(undefined);
      return;
    }

    let cancelled = false;

    productData
      .loadDataFromLocalCache(device)
      .then((objJson) => {
        if (!cancelled) {
          setCleanedRawProductData(cleanUpObjJson(objJson));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCleanedRawProductData(undefined);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [productData, device]);

  const initCatalog = async (fastCheck: boolean) => {
    setIsLoaded(false);
    let azureCatalogData: any;
    if (fastCheck) azureCatalogData = await ProductStorageHandler.loadCachedAllMetadataAsObject("import");
    else azureCatalogData = await ProductStorageHandler.getAllMetadata("import");
    if (azureCatalogData) {
      const productMap = new Map();
      const orderMap = new Map();

      for (const item of azureCatalogData) {
        if (item?.CatalogId) {
          productMap.set(item.CatalogId, item);
          productMap.set(`${item.OrderNumber}___${item.ApplicationVersion}`, item);

          const existingItem = orderMap.get(item.OrderNumber);
          if (!existingItem || compareVersions(item.ApplicationVersion, existingItem.ApplicationVersion) > 0) {
            orderMap.set(item.OrderNumber, item); // update if it's newer
          }
        }
      }

      setAzureProductDataMap(productMap);
      setAzureOrderNumberMap(orderMap);
    }
    setIsLoaded(true);
  };

  useEffect(() => {
    initCatalog(true);
  }, []);

  useEffect(() => {
    if (!device?.template || !productInfo) return;

    const decodedMetadata = getDecodedMetadata(productInfo, state.settings.uiLanguage);
    DeviceTemplate.updateVariantData(
      device.template,
      decodedMetadata,
      orderInformation?.articleNo || productInfo.OrderNumber,
    );
  }, [device, orderInformation?.articleNo, productInfo]);

  {
    /* ManufacturerId: Manufacturer.a.RefId,
        ProductId: data._productInfo.Id,
        CatalogId: data._productInfo.CatalogId,
        SerialNumber: data._productInfo.Hardware.SerialNumber,
        VersionNumber: data._productInfo.Hardware.VersionNumber,
        BusCurrent: data._productInfo.Hardware.BusCurrent,
        IsRailMounted: data._productInfo.IsRailMounted,
        OrderNumber: data._productInfo.OrderNumber,
        WidthInMillimeter: data._productInfo.WidthInMillimeter,
      }; */
  }

  if (productData == undefined || productInfo == undefined) {
    return <div>{state.translate("ui_KnxProductData.noProductData", "Keine Produktdaten")}</div>;
  }

  function cleanUpObjJson(objJson: any): any {
    const items: Array<any> = [];
    for (const i in objJson.items) {
      const childNode = objJson.items[i];
      items.push(cleanUpObjJson(childNode));
    }

    const cleanedNode: any = {
      items: items.length > 0 ? items : undefined,
      a: objJson.a,
      "#name": objJson["#name"],
    };

    return cleanedNode;
  }

  function getNodeChildrenByName(objJson: any, targetName: string): any | undefined {
    if (!objJson) {
      return undefined;
    }

    if (objJson && objJson["#name"] === targetName) {
      return objJson;
    }

    for (const i in objJson.items) {
      const item = objJson.items[i];
      const result = getNodeChildrenByName(item, targetName);
      if (result !== undefined) {
        return result;
      }
    }

    return undefined;
  }

  function stringifyInlineObject(obj: any): string {
    const entries = Object.entries(obj).filter(([, value]) => value !== undefined);
    if (entries.length === 0) return "{}";

    const parts = entries.map(([key, value]) => `${JSON.stringify(key)}: ${JSON.stringify(value)}`);
    return `{ ${parts.join(", ")} }`;
  }

  function stringifyWithInlineA(value: any, depth = 0): string {
    const indent = "  ".repeat(depth);
    const childIndent = "  ".repeat(depth + 1);

    if (value === null || typeof value !== "object") {
      return JSON.stringify(value);
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return "[]";

      const lines = value.map((item) => `${childIndent}${stringifyWithInlineA(item, depth + 1)}`);
      return `[
${lines.join(",\n")}
${indent}]`;
    }

    const entries = Object.entries(value).filter(([, v]) => v !== undefined);
    if (entries.length === 0) return "{}";

    const lines = entries.map(([key, entryValue]) => {
      const renderedValue =
        key === "a" && entryValue && typeof entryValue === "object" && !Array.isArray(entryValue)
          ? stringifyInlineObject(entryValue)
          : stringifyWithInlineA(entryValue, depth + 1);

      return `${childIndent}${JSON.stringify(key)}: ${renderedValue}`;
    });

    return `{
${lines.join(",\n")}
${indent}}`;
  }

  async function copyJsonToClipboard() {
    if (productData) {
      try {
        let objJson = await productData.loadDataFromLocalCache(device);
        objJson = cleanUpObjJson(objJson);
        const sJson = stringifyWithInlineA(objJson);

        navigator.clipboard.writeText(sJson);
      } catch (e) {
        showToast({ isError: true, text: "Failed to copy to clipboard: " + e });
      }
    }
  }

  async function copyCompactDebugToClipboard() {
    if (productData) {
      try {
        const objJson = cleanedRawProductData ?? cleanUpObjJson(await productData.loadDataFromLocalCache(device));
        const compactDebug = device ? formatKnxRawProductDataText(device, objJson) : "";

        navigator.clipboard.writeText(compactDebug);
      } catch (e) {
        showToast({ isError: true, text: "Failed to copy to clipboard: " + e });
      }
    }
  }

  async function copyXmlToClipboard() {
    if (productData) {
      try {
        navigator.clipboard.writeText((await productData.loadXmlstring()) || "");
      } catch (e) {
        showToast({ isError: true, text: "Failed to copy to clipboard: " + e });
      }
    }
  }

  async function copyValueToClipboard(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      showToast({ text: itx("ui_KnxProductData.copiedToClipboard", "Copied to clipboard") });
    } catch (e) {
      showToast({
        isError: true,
        text: `${itx("ui_KnxProductData.copyToClipboardFailed", "Failed to copy to clipboard")}: ${e}`,
      });
    }
  }

  let button: any | undefined;
  if (isUiModeDeveloper()) {
    button = (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "6px",
          marginTop: "8px",
        }}
      >
        <button
          className="button"
          style={{ margin: 0 }}
          onClick={() => {
            copyXmlToClipboard();
          }}
        >
          {
            "Copy Product-Xml to Clipboard" // no itx
          }
        </button>
        <button
          className="button"
          style={{ margin: 0 }}
          onClick={() => {
            copyJsonToClipboard();
          }}
        >
          {
            "Copy Product-Json to Clipboard" // no itx
          }
        </button>
        <button
          className="button"
          style={{ margin: 0 }}
          onClick={() => {
            copyCompactDebugToClipboard();
          }}
        >
          {
            "Copy Text to Clipboard" // no itx
          }
        </button>
        <button
          className="button"
          style={{ margin: 0 }}
          onClick={async () => {
            const objJson = cleanUpObjJson(await productData.loadDataFromLocalCache(device));
            const applicationProgramChildren =
              getNodeChildrenByName(
                objJson,
                "ApplicationProgram", // no itx
              ) || objJson;

            state.dialogManager.openDialog(
              <BaseDialog
                title={noItx("Raw Product Data Viewer")}
                noScroll
                noOkButton
                noAbortButton
                noPaddingForContent
                bodyNoMinWidth
              >
                {device && (
                  <KnxRawProductDataViewer device={device} applicationProgramChildren={applicationProgramChildren} />
                )}
              </BaseDialog>,
            );
          }}
        >
          View Product Data
        </button>
      </div>
    );
  }

  const productName = productInfo?.ProductName || productInfo?.DefaultProductName;
  if (productInfo?.ProductName == undefined) {
    productData.productInfo = getTranslatedMetadata(productData.productInfo, state.settings.uiLanguage);
  }

  const VersionStatus = () => {
    const excludedOrderNumbers = [
      "SCN-IP000.03", // no itx
      "SCN-IP000.02", // no itx
      "SCN-IP100.03", // no itx
      "SCN-IP100.02", // no itx
      "IPS300SREG", // no itx
      "216700", // no itx
      "2167 00", // no itx
      "TYFS121", // no itx
    ];
    if (!excludedOrderNumbers.includes(productInfo.OrderNumber)) {
      if (!isLoaded) {
        // you can return a loader, spinner, or null here
        return (
          <div style={{ textAlign: "center" }}>
            <i className={`las la-spinner la-spin`}></i> {itx("UIKnxProductData.loading", "Loading...")}
          </div>
        );
      }

      if (isLoaded && azureOrderNumberMap.size == 0) {
        return (
          <div
            style={{
              textAlign: "center",
            }}
          >
            <span
              style={{
                color: "#007bff",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={async () => {
                await initCatalog(false);
              }}
            >
              {itx("UIKnxProductData.checkForUpdates", "Check for Updates")}
            </span>
          </div>
        );
      }

      if (productInfo) {
        const { OrderNumber, ApplicationVersion, VersionNumber } = productInfo;
        const fullKey = `${OrderNumber}___${ApplicationVersion}`;

        if (azureOrderNumberMap.has(OrderNumber)) {
          const latest = azureOrderNumberMap.get(OrderNumber);
          const latestVersion = latest?.VersionNumber;
          const latestAppVersion = latest?.ApplicationVersion;

          const latestfullKey = `${OrderNumber}___${latestAppVersion}`;
          if (fullKey !== latestfullKey) {
            return (
              <span>
                <span
                  style={{
                    textAlign: "center",
                    backgroundColor: "#fff3cd",
                    borderRadius: "4px",
                    padding: "2px 4px",
                  }}
                  data-tooltip-id="tv-tooltip"
                  data-tooltip-content={itx(
                    "ServerTemplateList.newerVersionIsAvailable",
                    "A newer version is available.",
                  )}
                  data-tooltip-place="right"
                  data-tooltip-delay-show={100}
                >
                  (&#x2B06;&#xFE0F; {latestVersion} {`available`})
                </span>
                &nbsp;
                {/* {device != undefined && (
                <span
                  style={{
                    color: "#007bff",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                  onClick={async () => {
                    await updateDeviceProduct(latest, OrderNumber);
                  }}
                >
                  {itx("ServerTemplateList.update", "Update")}
                </span>
              )} */}
              </span>
            );
          }
        }
      }
    }

    return (
      <div
        style={{ textAlign: "center" }}
        data-tooltip-id="tv-tooltip"
        data-tooltip-content={itx("ServerTemplateList.latest", "Latest Version")}
        data-tooltip-place="right"
        data-tooltip-delay-show={100}
      >
        &#x2705;
      </div>
    );
  };

  async function updateDeviceProduct(product: any, articleNo: string) {
    if (device) {
      const language = state.settings.uiLanguage;
      const productInfo = getDecodedMetadata(product, language);

      let type = device.isActor
        ? "Actor" // no itx
        : device.isSensor
          ? "SensorNew" // no itx
          : device.isDecentralActor
            ? "DecentralActor" // no itx
            : "Button"; // no itx
      if (device.channelType == ChannelType.suply12V || device.channelType == ChannelType.suply24V) {
        type = "Other";
      }
      const templateGroup = getTemplateGroup(type); // no itx
      if (templateGroup) {
        const catalogId = productInfo?.CatalogId;
        const importedTemplateList = templateGroup.templates.items;
        let groupName = templateGroup.name;
        if (templateGroup.templateGroupType == DeviceClasses.Actor) {
          groupName = state.translate("components.Actor", "Aktor");
        } else if (templateGroup.templateGroupType == DeviceClasses.Button) {
          groupName = state.translate("components.Button", "Button");
        } else if (templateGroup.templateGroupType == DeviceClasses.DecentralActor) {
          groupName = state.translate("components.DecentralActor", "Dezentrale Aktor");
        }
        for (const [key, value] of importedTemplateList.entries()) {
          if (value.knxProductData.modelDataId == catalogId && value.orderInformation.articleNo == articleNo) {
            // set template if device already importet
            if (device) {
              device.setAndLoadTemplate(value.id);
            }
            showToast({
              text: state.translate("ServerTemplateList.T1AlreadyImported", "Device(s) already imported!", {
                T1: groupName,
              }),

              time: 4000,
            });
            return;
          }
        }
        const template = await DeviceTemplate.createFromServerData(templateGroup, catalogId, productInfo, articleNo);

        showToast({
          text: state.translate(
            "ServerTemplateList.yourT1hasBeenImportet",
            `Ihr ausgewählter ${groupName} wurde herruntergeladen`,
            { T1: groupName },
          ),
        });
        state.selectionManager.selectedObject = template;
        if (device) {
          device.setAndLoadTemplate(template.id);
        }
      }
    }
  }

  {
    // orderInformation: any = {
    //   eanNumber: 0,
    //   articleNo: "",
    //   shopId: "",
    //   imageLink: "",
    //   pageLink: "",
    //   variantName: {},
    //   variantDescription: {},
    //   price: "",
    //   availabilty: "",
    // };
  }
  return (
    <div>
      <ProductDataSection title={state.translate("ui_KnxProductData.productData", "Produktdaten")} />

      <FieldSet style={{ userSelect: "text" }}>
        {isUiModeProductAdmin() && (
          <Field label={itx("ui_KnxProductData.maskVersion", "Mask Version")}>{productInfo.MaskVersion}</Field>
        )}

        {isUiModeProductAdmin() && (
          <Field label={itx("ui_KnxProductData.manufacturerId ", "Manufacturer Id")}>
            {productInfo.ManufacturerId}
          </Field>
        )}

        {isUiModeProductAdmin() &&
          productInfo.OriginalManufacturer != "" &&
          productInfo.OriginalManufacturer != undefined && (
            <Field label={itx("ui_KnxProductData.origManufactu", "Original Manufacturer")}>
              {productInfo.OriginalManufacturer}
            </Field>
          )}
        <Field label={state.translate("ui_KnxProductData.manufacturerName", "Hersteller")}>
          {cleanDecode(productInfo.ManufacturerName || "")}
        </Field>
        <Field label={state.translate("ui_KnxProductData.productName", "Product")}>
          {orderInformation?.variantName?.["de-DE"] ?? cleanDecode(productName || "")}
        </Field>
        {productInfo.ProductDescription && (
          <Field label={state.translate("ui_KnxProductData.productDescription", "Beschreibung")}>
            <div style={{ width: "100%", whiteSpace: "break-spaces" }}>
              {cleanDecode(productInfo.ProductDescription || "")}
            </div>
          </Field>
        )}

        <Field label={state.translate("ui_KnxProductData.orderNumber", "Bestellnummer")}>
          {cleanDecode(orderInformation?.articleNo || productInfo.OrderNumber)}
        </Field>

        {isDeveloper && (
          <Field label={state.translate("ui_KnxProductData.serialNumber", "SerialNumber")}>
            {cleanDecode(productInfo.SerialNumber || "")}
          </Field>
        )}
        {isDeveloper && (
          <Field label={state.translate("ui_KnxProductData.versionNumber", "HardwareVersion")}>
            {`${productInfo.VersionNumber} ( ${productInfo.ApplicationVersion} )`}
          </Field>
        )}
        {isUiModeExpert() && (
          <Field label={state.translate("ui_KnxProductData.application", "Applikation")}>
            {productInfo?.Application ? (
              decodeURIComponent(productInfo.Application || "")
            ) : (
              <ImageAndName
                name={
                  productInfo?.Application
                    ? productInfo?.Application
                    : itx("ServerTemplateList.unknownApplication", "No Application availiable")
                }
                image={productInfo?.Application ? "" : state.hostBaseUrl + "images/warning.png"}
              />
            )}
          </Field>
        )}

        <Field label={state.translate("ui_KnxProductData.applicationVersion", "ProgramVersion")}>
          <div style={{ display: "flex", flexDirection: "row" }}>
            {ConvertVersion(productInfo.ApplicationVersion)} &nbsp;
            <VersionStatus />
          </div>
        </Field>

        <Field label={state.translate("ui_KnxProductData.widthInMillimeter", "Width (in mm)")}>
          {productInfo.IsRailMounted == "true" && productInfo.WidthInMillimeter
            ? `${productInfo.WidthInMillimeter || "0"} mm (${
                Math.ceil(parseFloat(productInfo.WidthInMillimeter || "0") / 9) / 2
              }M)`
            : state.translate("no", "No")}
        </Field>
        <Field label={state.translate("ui_KnxProductData.BusCurrent", "Busstrom")}>
          {productInfo.BusCurrent && parseFloat(productInfo.BusCurrent)}{" "}
          {state.translate("units.milliAmpsShort", "mA")}
        </Field>
        {isUiModeExpert() && (
          <Field label={state.translate("ui_KnxProductData.MediumType", "Medium Type")}>{productInfo.MediumType}</Field>
        )}
        {isUiModeExpert() && (
          <Field label={state.translate("ui_KnxProductData.Programmable", "Programmable")}>
            {productInfo?.Programmable}
          </Field>
        )}

        <UI_KnxProductVoltusData productData={productData} articleNo={orderInformation?.articleNo || productInfo.OrderNumber} />
        <TechnicalManualLink productData={productData} orderInformation={orderInformation} />
      </FieldSet>
      {button}

      {isDevelopmentEnvironment() && device?.template && (
        <div className="estadaColumn left">
          <div style={{ whiteSpace: "nowrap" }}>
            {noItx("modelDataId:")}
            {device?.template?.knxProductData.modelDataId || "!! no modelDataId !!"}
          </div>

          {!device?.template?.knxProductData.modelDataId && (
            <div>
              {noItx("ProductId:")} {(device?.template as any).ProductId}
            </div>
          )}

          {!device?.template?.knxProductData.modelDataId && (
            <div>
              {noItx("ApplicationProgramRefId:")} {(device?.template as any).ApplicationProgramRefId}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
